
import { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { collagenProducts } from '../../../mocks/products';

export default function CollagenProducts() {
  const titleReveal = useScrollReveal({ animation: 'fadeUp', delay: 0 });
  const [activeTab, setActiveTab] = useState<'all' | 'skin' | 'joint'>('all');

  const filteredProducts = activeTab === 'all'
    ? collagenProducts
    : collagenProducts.filter(p =>
      activeTab === 'skin'
        ? p.name.toLowerCase().includes('cilt') || p.name.toLowerCase().includes('güzellik')
        : p.name.toLowerCase().includes('eklem') || p.name.toLowerCase().includes('kemik')
    );

  return (
    <section className="bg-white py-12 sm:py-16 md:py-20 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-48 sm:w-96 h-48 sm:h-96 bg-gradient-to-br from-brand-100/30 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-48 sm:w-96 h-48 sm:h-96 bg-gradient-to-tr from-amber-100/30 to-transparent rounded-full blur-3xl" />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div ref={titleReveal.ref} className={`text-center mb-8 sm:mb-10 md:mb-12 ${titleReveal.className}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-50 to-amber-50 border border-brand-200/40 rounded-full px-4 sm:px-5 py-1.5 sm:py-2 mb-4 sm:mb-5">
            <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
              <i className="ri-heart-pulse-line text-brand-600 text-xs sm:text-sm"></i>
            </div>
            <span className="text-brand-700 text-[10px] sm:text-xs font-semibold tracking-wider uppercase">Premium Kolajen Serisi</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
            Gençlik ve <span className="bg-gradient-to-r from-brand-600 to-amber-500 bg-clip-text text-transparent">Sağlık Formülü</span>
          </h2>
          <p className="text-gray-500 text-xs sm:text-sm max-w-2xl mx-auto leading-relaxed px-2">
            Deniz kaynaklı premium kolajen ile cildinizi gençleştirin, eklemlerinizi güçlendirin
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex justify-center mb-6 sm:mb-8 md:mb-10">
          <div className="inline-flex items-center gap-1 sm:gap-2 bg-white rounded-xl sm:rounded-2xl p-1 sm:p-1.5 shadow-lg shadow-gray-200/50 border border-gray-100">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-[10px] sm:text-xs font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeTab === 'all'
                  ? 'bg-gradient-to-r from-brand-600 to-brand-500 text-white shadow-md shadow-brand-200'
                  : 'text-gray-500 hover:text-brand-700 hover:bg-brand-50'
              }`}
            >
              <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex items-center justify-center">
                <i className="ri-apps-2-line text-xs sm:text-sm"></i>
              </div>
              Tümü
            </button>
            <button
              onClick={() => setActiveTab('skin')}
              className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-[10px] sm:text-xs font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeTab === 'skin'
                  ? 'bg-gradient-to-r from-brand-600 to-brand-500 text-white shadow-md shadow-brand-200'
                  : 'text-gray-500 hover:text-brand-700 hover:bg-brand-50'
              }`}
            >
              <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex items-center justify-center">
                <i className="ri-sparkling-2-line text-xs sm:text-sm"></i>
              </div>
              <span className="hidden sm:inline">Cilt Güzelliği</span>
              <span className="sm:hidden">Cilt</span>
            </button>
            <button
              onClick={() => setActiveTab('joint')}
              className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-[10px] sm:text-xs font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeTab === 'joint'
                  ? 'bg-gradient-to-r from-brand-600 to-brand-500 text-white shadow-md shadow-brand-200'
                  : 'text-gray-500 hover:text-brand-700 hover:bg-brand-50'
              }`}
            >
              <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex items-center justify-center">
                <i className="ri-heart-pulse-line text-xs sm:text-sm"></i>
              </div>
              <span className="hidden sm:inline">Eklem & Kemik</span>
              <span className="sm:hidden">Eklem</span>
            </button>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-8 sm:mb-10 md:mb-12">
          {filteredProducts.map((product, index) => (
            <EnhancedCollagenCard key={product.id} product={product} index={index} />
          ))}
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-8 sm:mb-10 md:mb-12">
          <BenefitCard
            icon="ri-sparkling-2-line"
            title="Cilt Gençleştirme"
            description="Kırışıklıkları azaltır, cilt elastikiyetini artırır ve doğal parlaklık kazandırır"
            gradient="from-brand-500 to-cyan-500"
            delay={0}
          />
          <BenefitCard
            icon="ri-heart-pulse-line"
            title="Eklem Sağlığı"
            description="Eklem ağrılarını hafifletir, hareket kabiliyetini artırır ve kıkırdak yapısını destekler"
            gradient="from-amber-500 to-orange-500"
            delay={100}
          />
          <BenefitCard
            icon="ri-shield-check-line"
            title="Kemik Desteği"
            description="Kemik yoğunluğunu korur, kas-iskelet sistemini güçlendirir ve yaşlanma etkilerini azaltır"
            gradient="from-emerald-500 to-teal-500"
            delay={200}
          />
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500 rounded-xl sm:rounded-2xl p-5 sm:p-8 md:p-12 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 sm:w-64 h-32 sm:h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 sm:w-48 h-24 sm:h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center relative z-10">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 sm:px-4 py-1 sm:py-1.5 mb-3 sm:mb-4">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-information-line text-xs"></i>
                </div>
                <span className="text-[10px] sm:text-xs font-semibold">Bilgi</span>
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">Kolajen Nedir?</h3>
              <p className="text-brand-100 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">
                Kolajen, vücudumuzda doğal olarak bulunan ve cilt, eklem, kemik ve bağ dokularının yapısını oluşturan en önemli proteindir. Yaşla birlikte kolajen üretimi azalır.
              </p>
              <p className="text-brand-100 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6 hidden sm:block">
                Premium kolajen takviyelerimiz, deniz kaynaklı yüksek kaliteli kolajen içerir ve vücudunuzun ihtiyaç duyduğu desteği sağlar.
              </p>
              <a
                href="#"
                className="inline-flex items-center gap-2 bg-white text-brand-700 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm hover:bg-brand-50 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Detaylı Bilgi
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </a>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
              <StatCard number="25+" label="Yaş Sonrası" sublabel="Kolajen azalır" />
              <StatCard number="30%" label="Azalma" sublabel="40 yaşında" />
              <StatCard number="60%" label="Azalma" sublabel="60 yaşında" />
              <StatCard number="100%" label="Doğal" sublabel="Deniz kaynaklı" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function EnhancedCollagenCard({ product, index }: { product: any; index: number }) {
  const reveal = useScrollReveal({ animation: 'zoomIn', delay: index * 80 });
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div ref={reveal.ref} className={reveal.className}>
      <a
        href="#"
        className="group block relative bg-white rounded-xl sm:rounded-2xl overflow-hidden border border-gray-100 hover:border-brand-200/60 transition-all duration-500 hover:shadow-2xl hover:shadow-brand-100/40 cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
          transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Image */}
        <div className="relative w-full h-40 sm:h-52 md:h-64 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain object-center p-2 sm:p-4 transition-all duration-700"
            style={{
              transform: isHovered ? 'scale(1.1) translateY(-4px)' : 'scale(1)',
            }}
          />

          {/* Hover overlay */}
          <div
            className="absolute inset-0 bg-gradient-to-t from-brand-900/95 via-brand-900/80 to-transparent flex-col justify-end p-3 sm:p-4 transition-opacity duration-500 hidden sm:flex"
            style={{ opacity: isHovered ? 1 : 0 }}
          >
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex items-center gap-1.5 sm:gap-2 text-white text-[10px] sm:text-xs">
                <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex items-center justify-center">
                  <i className="ri-check-line text-[10px] sm:text-xs"></i>
                </div>
                <span>Deniz Kaynaklı</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-white text-[10px] sm:text-xs">
                <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex items-center justify-center">
                  <i className="ri-check-line text-[10px] sm:text-xs"></i>
                </div>
                <span>Yüksek Emilim</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-white text-[10px] sm:text-xs">
                <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex items-center justify-center">
                  <i className="ri-check-line text-[10px] sm:text-xs"></i>
                </div>
                <span>Klinik Test Edilmiş</span>
              </div>
            </div>
          </div>

          {/* Badge */}
          {product.badge && (
            <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[8px] sm:text-[10px] font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-md sm:rounded-lg shadow-lg">
              {product.badge}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 md:p-5">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <span className="text-brand-600 text-[8px] sm:text-[10px] font-semibold uppercase tracking-wider">Kolajen</span>
            <div className="flex items-center gap-0.5 sm:gap-1">
              <i className="ri-star-fill text-amber-400 text-[10px] sm:text-xs"></i>
              <span className="text-gray-700 text-[10px] sm:text-xs font-bold">{product.rating}</span>
            </div>
          </div>

          <h3 className="font-bold text-gray-900 text-xs sm:text-sm mb-0.5 sm:mb-1 group-hover:text-brand-700 transition-colors line-clamp-1">
            {product.name}
          </h3>
          <p className="text-gray-400 text-[10px] sm:text-xs mb-2 sm:mb-3 line-clamp-2 hidden sm:block">{product.description}</p>

          <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-gray-100">
            <div>
              {product.oldPrice && (
                <span className="text-gray-400 text-[10px] sm:text-xs line-through block">{product.oldPrice}</span>
              )}
              <span className="text-gray-900 font-bold text-sm sm:text-base">{product.price}</span>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-brand-600 to-brand-500 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shadow-brand-200/50 transition-all duration-500 hover:shadow-brand-300/50 hover:scale-105 cursor-pointer">
              <i className="ri-shopping-cart-2-line text-white text-sm sm:text-lg"></i>
            </div>
          </div>
        </div>
      </a>
    </div>
  );
}

function BenefitCard({
  icon,
  title,
  description,
  gradient,
  delay,
}: {
  icon: string;
  title: string;
  description: string;
  gradient: string;
  delay: number;
}) {
  const reveal = useScrollReveal({ animation: 'fadeUp', delay });

  return (
    <div ref={reveal.ref} className={reveal.className}>
      <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-gray-100 hover:border-brand-200/60 hover:shadow-xl transition-all duration-500 group cursor-pointer">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-gradient-to-br ${gradient} rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-500`}>
          <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex items-center justify-center">
            <i className={`${icon} text-white text-base sm:text-lg md:text-xl`}></i>
          </div>
        </div>
        <h4 className="font-bold text-gray-900 text-sm sm:text-base mb-1 sm:mb-2 group-hover:text-brand-700 transition-colors">
          {title}
        </h4>
        <p className="text-gray-500 text-[10px] sm:text-xs leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}

function StatCard({ number, label, sublabel }: { number: string; label: string; sublabel: string }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/20">
      <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-0.5 sm:mb-1">{number}</div>
      <div className="text-brand-100 text-[10px] sm:text-xs font-semibold">{label}</div>
      <div className="text-brand-200/60 text-[9px] sm:text-[10px] mt-0.5">{sublabel}</div>
    </div>
  );
}
